import { ChangeDetectionStrategy, Component, computed, effect, input, signal } from '@angular/core';

import { TranslatePipe } from '@core/i18n-legacy';
import type { TimelineEvent } from '@core/tellent-assistant-util';
import { ButtonComponent, IconComponent } from '@core/user-interface-legacy';

import { TellentAssistantBrandMarkComponent } from '../../brand-mark/tellent-assistant-brand-mark.component';
import { TellentAssistantThinkingIndicatorComponent } from '../../thinking-indicator/tellent-assistant-thinking-indicator.component';
import { TellentAssistantTextPartComponent } from '../text-part/text-part.component';

export type ThinkingRunState = 'running' | 'done';

@Component({
  selector: 'tellent-assistant-thinking-section',
  imports: [
    ButtonComponent,
    IconComponent,
    TellentAssistantBrandMarkComponent,
    TellentAssistantTextPartComponent,
    TellentAssistantThinkingIndicatorComponent,
    TranslatePipe,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './thinking-section.component.html',
  styleUrl: './thinking-section.component.less',
})
export class TellentAssistantThinkingSectionComponent {
  protected readonly userExpanded = signal<boolean | null>(null);

  protected readonly toolCount = computed(
    () => this.timeline().filter(event => event.kind === 'tool').length,
  );

  protected readonly hasContent = computed(() => this.timeline().length > 0);

  protected readonly showThinkingShimmer = computed(() => {
    if (this.runState() !== 'running') return false;
    const events = this.timeline();
    const last = events[events.length - 1];
    return last?.kind === 'tool' && last.status === 'done';
  });

  protected readonly summaryKey = computed(() =>
    this.toolCount() > 0
      ? 'tlc.tellent_assistant.tool_call.steps_completed'
      : 'tlc.tellent_assistant.tool_call.thinking_status',
  );

  protected readonly expanded = computed(() => {
    const override = this.userExpanded();
    if (override !== null) return override;
    return this.runState() === 'running';
  });

  protected readonly resetOverrideOnRunStart = effect(() => {
    if (this.runState() === 'running') this.userExpanded.set(null);
  });

  public readonly timeline = input.required<readonly TimelineEvent[]>();
  public readonly runState = input.required<ThinkingRunState>();

  protected toggle(): void {
    this.userExpanded.set(!this.expanded());
  }
}
